import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { apiClient } from '../api/client';
import type { SiteReview } from '@audit/shared-types';

const STATUS_COLOURS: Record<string, string> = {
  complete: 'bg-green-100 text-green-700',
  running: 'bg-yellow-100 text-yellow-700',
  pending: 'bg-gray-100 text-gray-600',
  failed: 'bg-red-100 text-red-700',
};

type StatusFilter = 'all' | 'not-run' | 'pending' | 'running' | 'complete' | 'failed';
type IssueFilter = 'all' | 'with-issues' | 'no-issues';

export default function SiteReviewPage() {
  const { siteId } = useParams<{ siteId: string }>();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [issueFilter, setIssueFilter] = useState<IssueFilter>('all');

  const { data, isLoading, error } = useQuery({
    queryKey: ['site-review', siteId],
    queryFn: () => apiClient.get<SiteReview>(`/sites/${siteId ?? ''}/review`).then((r) => r.data),
    enabled: !!siteId,
    refetchInterval: (query) => {
      const pages = query.state.data?.pages ?? [];
      return pages.some((page) => {
        const status = page.latestAudit?.status;
        return status === 'pending' || status === 'running';
      })
        ? 5000
        : false;
    },
  });

  const siteReviewMutation = useMutation({
    mutationFn: async (force: boolean) => {
      if (!siteId) throw new Error('siteId is required');
      return apiClient.post(`/sites/${siteId}/review/run`, { force });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['site-review', siteId] });
      await queryClient.invalidateQueries({ queryKey: ['audits', 'recent'] });
    },
  });

  const filteredPages = useMemo(() => {
    if (!data) {
      return [];
    }

    const query = search.trim().toLowerCase();

    return data.pages.filter((page) => {
      const matchesSearch =
        query.length === 0 ||
        page.title.toLowerCase().includes(query) ||
        page.slug.toLowerCase().includes(query) ||
        page.url.toLowerCase().includes(query);

      const pageStatus = page.latestAudit?.status ?? 'not-run';
      const matchesStatus = statusFilter === 'all' || pageStatus === statusFilter;

      const issueCount = page.latestAudit?.issueCount ?? 0;
      const matchesIssueFilter =
        issueFilter === 'all' ||
        (issueFilter === 'with-issues' && issueCount > 0) ||
        (issueFilter === 'no-issues' && issueCount === 0);

      return matchesSearch && matchesStatus && matchesIssueFilter;
    });
  }, [data, issueFilter, search, statusFilter]);

  if (isLoading) return <p className="text-sm text-gray-500">Loading site review…</p>;
  if (error || !data) return <p className="text-sm text-red-600">Failed to load site review</p>;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-brand-700">Site Review</p>
          <h2 className="text-3xl font-bold text-gray-900 mt-1">{data.site.name}</h2>
          <p className="text-sm text-gray-500 mt-2">{data.site.url}</p>
        </div>

        <div className="flex gap-3">
          <Link
            to={`/sites/${data.site.id}/pages`}
            className="text-sm border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50"
          >
            View pages
          </Link>
          <button
            onClick={() => void siteReviewMutation.mutateAsync(false)}
            disabled={siteReviewMutation.isPending || data.summary.totalPages === 0}
            className="text-sm border border-brand-200 text-brand-700 px-4 py-2 rounded-lg hover:bg-brand-50 disabled:opacity-50"
          >
            {siteReviewMutation.isPending && siteReviewMutation.variables === false ? 'Queueing…' : 'Run changed pages'}
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-5">
        <div>
          <p className="text-sm font-semibold text-gray-700">Audit Scope</p>
          <p className="text-sm text-gray-500 mt-1">
            Site reviews now run against all WCAG levels (`A`, `AA`, `AAA`) and both supported versions (`2.1` and `2.2`) by default.
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Use <strong>Run changed pages</strong> to queue only new or updated pages.
          </p>
        </div>

        {siteReviewMutation.isError && (
          <p className="text-sm text-red-600">Failed to start site review.</p>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
        <MetricCard label="Pages" value={String(data.summary.totalPages)} />
        <MetricCard label="Audited" value={String(data.summary.auditedPages)} />
        <MetricCard label="Pages With Issues" value={String(data.summary.pagesWithIssues)} />
        <MetricCard label="Open Issues" value={String(data.summary.totalIssues)} />
        <MetricCard label="Latest Audit" value={data.summary.latestAuditAt ? new Date(data.summary.latestAuditAt).toLocaleDateString() : 'Never'} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SeverityCard label="Critical" value={data.summary.criticalCount} className="bg-red-50 text-red-700 border-red-100" />
        <SeverityCard label="Serious" value={data.summary.seriousCount} className="bg-orange-50 text-orange-700 border-orange-100" />
        <SeverityCard label="Moderate" value={data.summary.moderateCount} className="bg-yellow-50 text-yellow-700 border-yellow-100" />
        <SeverityCard label="Minor" value={data.summary.minorCount} className="bg-gray-50 text-gray-700 border-gray-200" />
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-700">Latest Result Per Page</h3>
            <p className="text-xs text-gray-500 mt-1">Review coverage across the whole site from one screen.</p>
          </div>
          <p className="text-xs text-gray-500">Pending: {data.summary.pendingPages} • Failed: {data.summary.failedPages}</p>
        </div>

        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 grid gap-3 md:grid-cols-[minmax(0,2fr)_auto_auto]">
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Filter by page title, slug, or URL"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-brand-500 focus:outline-none"
          />

          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value as StatusFilter)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 bg-white focus:border-brand-500 focus:outline-none"
          >
            <option value="all">All statuses</option>
            <option value="not-run">Not run</option>
            <option value="pending">Pending</option>
            <option value="running">Running</option>
            <option value="complete">Complete</option>
            <option value="failed">Failed</option>
          </select>

          <select
            value={issueFilter}
            onChange={(event) => setIssueFilter(event.target.value as IssueFilter)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 bg-white focus:border-brand-500 focus:outline-none"
          >
            <option value="all">All issue states</option>
            <option value="with-issues">With issues</option>
            <option value="no-issues">No issues</option>
          </select>
        </div>

        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 uppercase bg-gray-50">
              <th className="px-6 py-3">Page</th>
              <th className="px-6 py-3">Latest Audit</th>
              <th className="px-6 py-3">Status</th>
              <th className="px-6 py-3">Issues</th>
              <th className="px-6 py-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredPages.map((page) => (
              <tr key={page.pageId} className="border-t border-gray-100 hover:bg-gray-50 align-top">
                <td className="px-6 py-3">
                  <p className="font-medium text-gray-900">{page.title}</p>
                  <p className="text-xs text-gray-500 mt-1">{page.slug} • {page.type}</p>
                </td>
                <td className="px-6 py-3 text-gray-500">
                  {page.latestAudit ? new Date(page.latestAudit.createdAt).toLocaleString() : 'Never'}
                </td>
                <td className="px-6 py-3">
                  {page.latestAudit ? (
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLOURS[page.latestAudit.status] ?? 'bg-gray-100 text-gray-600'}`}>
                      {page.latestAudit.status}
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">not run</span>
                  )}
                </td>
                <td className="px-6 py-3 text-gray-700">{page.latestAudit?.issueCount ?? '—'}</td>
                <td className="px-6 py-3">
                  <div className="flex flex-col items-start gap-1">
                    {page.latestAudit && (
                      <Link to={`/audits/${page.latestAudit.id}`} className="text-xs text-brand-600 hover:text-brand-700 font-medium">
                        Open latest audit
                      </Link>
                    )}
                    <Link to={`/sites/${data.site.id}/pages/${page.pageId}/audit`} className="text-xs text-gray-600 hover:text-gray-900 font-medium">
                      Run page audit
                    </Link>
                  </div>
                </td>
              </tr>
            ))}
            {filteredPages.length === 0 && (
              <tr>
                <td colSpan={5} className="px-6 py-8 text-sm text-center text-gray-500">
                  No pages match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</p>
      <p className="text-2xl font-bold text-gray-900 mt-2">{value}</p>
    </div>
  );
}

function SeverityCard({ label, value, className }: { label: string; value: number; className: string }) {
  return (
    <div className={`border rounded-xl p-4 ${className}`}>
      <p className="text-xs font-semibold uppercase tracking-wide">{label}</p>
      <p className="text-2xl font-bold mt-2">{value}</p>
    </div>
  );
}